import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Pencil, ChevronDown, ChevronUp, ChevronRight, CloudUpload, Trash2, MapPin } from 'lucide-react';
import { groupConcepts, hasAreas, SIN_AREA, SIN_PARTIDA } from '@/components/ConceptGroupPicker';
import { toast } from 'sonner';
import { mergeLocalConcepts } from '@/offline/concepts';
import { offlineDb } from '@/offline/db';
import { syncManager } from '@/offline/syncManager';
import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const formatMXN = (value) =>
  `$${(Number(value) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* Lista de conceptos de un contrato con botón para editar PU y eliminar (Admin). Incluye conceptos locales pendientes */
export const ContractConceptsList = ({ projectId, refreshKey, onEdit, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [concepts, setConcepts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncTick, setSyncTick] = useState(0);
  const [toDelete, setToDelete] = useState(null); // { concept, usage }
  const [deleting, setDeleting] = useState(false);
  const [progress, setProgress] = useState({ byId: {}, totals: null }); // Importe Ejecutado por concepto
  const [collapsed, setCollapsed] = useState({}); // áreas colapsadas en la vista agrupada

  const loadProgress = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/concepts-progress`, { timeout: 8000 });
      const byId = {};
      (res.data.concepts || []).forEach((r) => { byId[r.concept_id] = r; });
      setProgress({ byId, totals: res.data.totals });
    } catch (err) {
      console.error('Error cargando importe ejecutado:', err);
    }
  };

  const askDelete = async (concept) => {
    if (concept.local) {
      setToDelete({ concept, usage: { captures: 0, figures: 0 } });
      return;
    }
    try {
      const res = await axios.get(`${API}/concepts/${concept.id}/usage`);
      setToDelete({ concept, usage: res.data });
    } catch (err) {
      toast.error('No se pudo verificar el uso del concepto');
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    const { concept, usage } = toDelete;
    setDeleting(true);
    try {
      if (concept.local) {
        await offlineDb.deleteConcept(concept.id);
        syncManager.refreshPending();
        syncManager.notifyConceptsChanged();
      } else {
        const force = usage.captures > 0 || usage.figures > 0;
        await axios.delete(`${API}/concepts/${concept.id}`, { params: force ? { force: true } : {} });
      }
      toast.success(`Concepto ${concept.clave} eliminado`);
      setConcepts((prev) => prev.filter((c) => c.id !== concept.id));
      setToDelete(null);
      onDeleted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar el concepto');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const handler = () => setSyncTick((t) => t + 1);
    syncManager.addEventListener('concepts-changed', handler);
    return () => syncManager.removeEventListener('concepts-changed', handler);
  }, []);

  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    setLoading(true);
    loadProgress();
    axios.get(`${API}/concepts`, { params: { project_id: projectId }, timeout: 8000 })
      .then((res) => res.data || [])
      .catch((err) => { console.error('Error cargando conceptos:', err); return []; })
      .then((server) => mergeLocalConcepts(projectId, server))
      .then((all) => { if (!cancelled) setConcepts(all); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, projectId, refreshKey, syncTick]);

  const grouped = hasAreas(concepts);

  const renderRow = (c) => (
                  <tr key={c.id} className="border-t border-[#1E3550]/60 text-[#E2EAF4]" data-testid={`admin-concept-row-${c.id}`}>
                    <td className="px-3 py-2 font-mono text-[#00CFFF] whitespace-nowrap">
                      {c.clave}
                      {!grouped && (c.area || c.partida) && (
                        <span className="block text-[9px] text-[#5A7A9A]">{[c.area, c.partida].filter(Boolean).join(' · ')}</span>
                      )}
                      {c.local && (
                        <span className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-[#FF6B00]/15 px-1.5 text-[9px] font-semibold text-[#FF6B00]" title="Guardado en el dispositivo, pendiente de sincronizar" data-testid={`concept-local-badge-${c.id}`}>
                          <CloudUpload className="w-2.5 h-2.5" /> local
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 max-w-[180px] truncate" title={c.descripcion}>{c.descripcion}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">{c.contratado} {c.unidad}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap" data-testid={`concept-pu-${c.id}`}>{formatMXN(c.precio_unitario)}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap text-[#22C55E]" data-testid={`concept-importe-${c.id}`}>{formatMXN(c.importe)}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap text-[#00CFFF]" data-testid={`concept-ejecutado-${c.id}`} title={progress.byId[c.id] ? `Acumulado ${progress.byId[c.id].acumulado} ${c.unidad} × PU` : ''}>
                      {formatMXN(progress.byId[c.id]?.importe_ejecutado || 0)}
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => onEdit(c)}
                        className="p-1.5 rounded-md text-[#A855F7] hover:bg-[#A855F7]/15 transition-colors"
                        title="Editar concepto"
                        data-testid={`btn-edit-concept-${c.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => askDelete(c)}
                        className="p-1.5 rounded-md text-red-400 hover:bg-red-500/15 transition-colors"
                        title="Eliminar concepto"
                        data-testid={`btn-delete-concept-${c.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
  );

  return (
    <div className="mt-3 border-t border-[#A855F7]/20 pt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-sm text-[#A855F7] hover:text-[#A855F7]/80"
        data-testid="btn-toggle-concepts-list"
      >
        <span>{open ? 'Ocultar conceptos' : 'Ver / editar conceptos'}</span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-[#1E3550] bg-[#0A1422]" data-testid="contract-concepts-list">
          {loading ? (
            <p className="p-3 text-sm text-[#5A7A9A]">Cargando conceptos...</p>
          ) : concepts.length === 0 ? (
            <p className="p-3 text-sm text-[#5A7A9A]" data-testid="concepts-empty">Sin conceptos en este contrato</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[#0D1520] text-[#5A7A9A]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Clave</th>
                  <th className="text-left px-2 py-2 font-medium">Descripción</th>
                  <th className="text-right px-2 py-2 font-medium">Cant.</th>
                  <th className="text-right px-2 py-2 font-medium">PU</th>
                  <th className="text-right px-2 py-2 font-medium">Importe</th>
                  <th className="text-right px-2 py-2 font-medium text-[#00CFFF]">Ejecutado</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {!grouped && concepts.map(renderRow)}
                {grouped && Object.entries(groupConcepts(concepts)).map(([area, byPartida]) => {
                  const areaConcepts = Object.values(byPartida).flat();
                  const areaImporte = areaConcepts.reduce((a, c) => a + (Number(c.importe) || 0), 0);
                  const areaEjecutado = areaConcepts.reduce((a, c) => a + (progress.byId[c.id]?.importe_ejecutado || 0), 0);
                  const isCollapsed = !!collapsed[area];
                  const partidaKeys = Object.keys(byPartida);
                  const showPartidas = partidaKeys.length > 1 || partidaKeys[0] !== SIN_PARTIDA;
                  return (
                    <React.Fragment key={area}>
                      <tr className="bg-[#0D1B2E] text-[#E2EAF4] cursor-pointer select-none" onClick={() => setCollapsed((o) => ({ ...o, [area]: !isCollapsed }))} data-testid={`area-row-${area}`}>
                        <td className="px-3 py-2 font-semibold whitespace-nowrap" colSpan={4}>
                          <span className="inline-flex items-center gap-1.5">
                            {isCollapsed ? <ChevronRight className="w-3.5 h-3.5 text-[#5A7A9A]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#5A7A9A]" />}
                            <MapPin className="w-3.5 h-3.5 text-[#00CFFF]" />
                            {area === SIN_AREA ? <span className="text-[#5A7A9A] italic">{area}</span> : area}
                            <span className="text-[10px] font-normal text-[#5A7A9A]">{areaConcepts.length} concepto{areaConcepts.length !== 1 ? 's' : ''}</span>
                          </span>
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap text-[#22C55E]" data-testid={`area-importe-${area}`}>{formatMXN(areaImporte)}</td>
                        <td className="px-2 py-2 text-right whitespace-nowrap text-[#00CFFF]">{formatMXN(areaEjecutado)}</td>
                        <td></td>
                      </tr>
                      {!isCollapsed && partidaKeys.map((partida) => (
                        <React.Fragment key={`${area}-${partida}`}>
                          {showPartidas && (
                            <tr className="bg-[#0A1422] text-[#A855F7]" data-testid={`partida-row-${area}-${partida}`}>
                              <td className="px-3 py-1.5 pl-8 text-[11px] font-semibold uppercase tracking-wide" colSpan={7}>
                                {partida === SIN_PARTIDA ? <span className="text-[#5A7A9A] italic normal-case">{partida}</span> : partida}
                                <span className="ml-2 text-[10px] font-normal text-[#5A7A9A] normal-case">{byPartida[partida].length}</span>
                              </td>
                            </tr>
                          )}
                          {byPartida[partida].map(renderRow)}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
              {progress.totals && (
                <tfoot className="sticky bottom-0 bg-[#0D1520] border-t border-[#1E3550]">
                  <tr className="text-[#E2EAF4] font-semibold" data-testid="concepts-totals-row">
                    <td className="px-3 py-2" colSpan={4}>
                      TOTAL CONTRATO
                      <span className="ml-2 text-[10px] font-normal text-[#5A7A9A]">
                        {progress.totals.avance_importe.toFixed(1)}% ejecutado en MXN
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap text-[#22C55E]" data-testid="concepts-total-importe">{formatMXN(progress.totals.importe_contratado)}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap text-[#00CFFF]" data-testid="concepts-total-ejecutado">{formatMXN(progress.totals.importe_ejecutado)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      )}

      <ConfirmDeleteDialog
        open={!!toDelete}
        loading={deleting}
        testId="confirm-delete-concept"
        title={`Eliminar concepto ${toDelete?.concept?.clave || ''}`}
        description={
          toDelete?.usage && (toDelete.usage.captures > 0 || toDelete.usage.figures > 0)
            ? `Este concepto tiene ${toDelete.usage.captures} foto(s) y ${toDelete.usage.figures} figura(s). Se eliminarán también todas ellas.`
            : 'Este concepto no tiene fotos ni figuras asociadas.'
        }
        onCancel={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default ContractConceptsList;
